import React, { useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import Select from 'react-select';
import { statusOptions } from '../config/data';
import { useMutation, useQueryClient } from 'react-query';
import { UPDATE_TASK_STATUS } from 'services/api/task';
import toast from 'react-hot-toast';
import { StatusUpdateModalProps } from '../types/types';

const StatusUpdateModal: React.FC<StatusUpdateModalProps> = ({
  show,
  onHide,
  taskId,
  currentStatus
}) => {
  const [selectedStatus, setSelectedStatus] = useState(
    statusOptions.find(option => option.value === currentStatus) ||
      statusOptions[0]
  );
  const queryClient = useQueryClient();

  const updateStatusMutation = useMutation(
    (data: { taskId: string; status: string }) => UPDATE_TASK_STATUS(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['tasks']);
        queryClient.invalidateQueries(['filteredTasks']);
        toast.success('Task status updated successfully');
        onHide();
      },
      onError: (error: any) => {
        toast.error(
          error?.response?.data?.messages || 'Failed to update task status'
        );
      }
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedStatus.value !== currentStatus) {
      updateStatusMutation.mutate({
        taskId,
        status: selectedStatus.value
      });
    } else {
      onHide();
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Update Task Status</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Status</Form.Label>
            <Select
              options={statusOptions}
              value={selectedStatus}
              onChange={option =>
                setSelectedStatus(option as typeof selectedStatus)
              }
              className="status-select"
              classNamePrefix="status-select"
              styles={{
                control: baseStyles => ({
                  ...baseStyles,
                  backgroundColor: '#141824',
                  borderColor: '#dee2e6'
                }),
                menu: baseStyles => ({
                  ...baseStyles,
                  backgroundColor: '#141824',
                  zIndex: 1000
                }),
                option: (baseStyles, state) => ({
                  ...baseStyles,
                  backgroundColor: state.isFocused ? '#2c3344' : '#141824',
                  color: 'white',
                  padding: '10px'
                }),
                singleValue: baseStyles => ({
                  ...baseStyles,
                  color: 'white'
                }),
                input: baseStyles => ({
                  ...baseStyles,
                  color: 'white'
                })
              }}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={updateStatusMutation.isLoading}
          >
            {updateStatusMutation.isLoading ? 'Updating...' : 'Update Status'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default StatusUpdateModal;
